// Small pieces shared by the Setup editors.

import type { ReactNode } from 'react';
import type { Skill } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { InlineError } from '../../components/ui.tsx';

/** ISO instant → value for <input type="datetime-local"> in the browser's zone. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function localInputToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function moveItem<T>(list: T[], i: number, delta: number): T[] {
  const j = i + delta;
  if (j < 0 || j >= list.length) return list;
  const out = list.slice();
  const a = out[i];
  const b = out[j];
  if (a === undefined || b === undefined) return list;
  out[i] = b;
  out[j] = a;
  return out;
}

export function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <span className="tog" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

export function UpDown({ i, n, onMove, disabled, what }: { i: number; n: number; onMove: (delta: number) => void; disabled?: boolean; what: string }) {
  return (
    <span className="updown">
      <button type="button" className="btn icon sm" onClick={() => onMove(-1)} disabled={disabled || i === 0} aria-label={`Move ${what} up`} title="Move up">
        <Icon name="up" />
      </button>
      <button type="button" className="btn icon sm" onClick={() => onMove(1)} disabled={disabled || i === n - 1} aria-label={`Move ${what} down`} title="Move down">
        <Icon name="down" />
      </button>
    </span>
  );
}

/** Save bar under an editor: dirty state, error, saved confirmation. */
export function SaveBar({
  canEdit,
  dirty,
  busy,
  error,
  saved,
  onSave,
  onReset,
  label = 'Save',
  extra,
}: {
  canEdit: boolean;
  dirty: boolean;
  busy: boolean;
  error: unknown;
  saved: boolean;
  onSave: () => void;
  onReset: () => void;
  label?: string;
  extra?: ReactNode;
}) {
  if (!canEdit) return <div className="note"><Icon name="lock" /> Read-only: only admins can change setup.</div>;
  return (
    <div className="savebar">
      <button type="button" className="btn pri" onClick={onSave} disabled={!dirty || busy}>
        <Icon name="check" />
        {busy ? 'Saving…' : label}
      </button>
      <button type="button" className="btn" onClick={onReset} disabled={!dirty || busy}>
        Discard changes
      </button>
      {extra}
      {dirty && !busy && <span className="note warn-t">Unsaved changes</span>}
      {saved && !dirty && !error && <span className="ok-t small">Saved</span>}
      <InlineError error={error} />
    </div>
  );
}

export const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** Toggle chips for skills (Phase 2): users' skills and a site's required skills. Unknown codes still show. */
export function SkillPicker({ skills, value, onChange, disabled, label }: { skills: Skill[]; value: string[]; onChange: (v: string[]) => void; disabled?: boolean; label: string }) {
  const known = new Set(skills.map((s) => s.code));
  const all = [...skills, ...value.filter((c) => !known.has(c)).map((c) => ({ code: c, label: c }))];
  if (all.length === 0) return <span className="note">No skills defined (Setup → Groups and routing).</span>;
  return (
    <div className="chips" role="group" aria-label={label}>
      {all.map((s) => {
        const on = value.includes(s.code);
        return (
          <button
            key={s.code}
            type="button"
            className={`daybtn skillbtn${on ? ' on' : ''}`}
            aria-pressed={on}
            disabled={disabled}
            title={s.code}
            onClick={() => onChange(on ? value.filter((x) => x !== s.code) : [...value, s.code])}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}
